/**
 * Review engine for AI edits: works directly on the ProseMirror document instead of
 * re-serializing HTML for every accept/reject.
 *
 * A review document contains `diffDelete` / `diffInsert` marks (inline changes) and
 * `diffBlock="insert|delete"` node attributes (whole blocks and atoms such as images and
 * Mermaid diagrams), each tagged with a change id.
 *
 * Undo model: before a review starts the host keeps the pre-review EditorState (doc +
 * history). While reviewing, documents are swapped with addToHistory:false. On finish the
 * saved state is restored and the resolved document is applied as ONE history step, so
 * Ctrl/Cmd+Z returns to the exact pre-review document and never to a diff-span state.
 */
import { createDocument, getHTMLFromFragment } from '@tiptap/core';
import { Fragment, Mark } from '@tiptap/pm/model';
import { EditorState } from '@tiptap/pm/state';
import { tokenizeHtmlForDiff, diffPartValueToString } from '../../utils/htmlDiffUtils';
import * as Diff from 'diff';

/** Transaction meta key used for review bookkeeping transactions. */
export const REVIEW_META = 'quillReview';

const BLOCK_SELECTOR = [
  'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'blockquote', 'ul', 'ol', 'table', 'tr', 'img', 'hr', 'pre',
  'div[data-mermaid-block]', 'div[data-tpl-field]', 'div[data-field-id]', 'section[data-trust-section]',
].join(',');

/**
 * Shorten text for change summaries.
 * @param {string} text - Text.
 * @param {number} [max=60] - Max length.
 * @returns {string} Trimmed text.
 */
const clip = (text, max = 60) => {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
};

/**
 * Whether a node carries a review block attribute.
 * @param {import('@tiptap/pm/model').Node} node - Node.
 * @returns {boolean} True when `diffBlock` is set.
 */
const hasDiffBlock = (node) => Boolean(node.attrs && node.attrs.diffBlock && node.attrs.diffChangeId);

/**
 * List the pending changes of a review document in document order.
 * @param {import('@tiptap/pm/model').Node} doc - Review document.
 * @returns {Array<{id: string, kind: 'insert'|'delete'|'replace', summary: string, pos: number}>} Changes.
 */
export function collectReviewChanges(doc) {
  const byId = new Map();
  const ensure = (id, pos) => {
    const key = String(id);
    if (!byId.has(key)) byId.set(key, { id: key, pos, del: '', ins: '', hasDelete: false, hasInsert: false });
    return byId.get(key);
  };
  doc.descendants((node, pos) => {
    if (hasDiffBlock(node)) {
      const change = ensure(node.attrs.diffChangeId, pos);
      const text = node.textContent || (node.type.name === 'image' ? '[image]' : `[${node.type.name}]`);
      if (node.attrs.diffBlock === 'delete') {
        change.hasDelete = true;
        change.del += ` ${text}`;
      } else {
        change.hasInsert = true;
        change.ins += ` ${text}`;
      }
      return false;
    }
    if (node.isInline) {
      node.marks.forEach((mark) => {
        const isDelete = mark.type.name === 'diffDelete';
        const isInsert = mark.type.name === 'diffInsert';
        if ((!isDelete && !isInsert) || !mark.attrs.changeId) return;
        const change = ensure(mark.attrs.changeId, pos);
        const text = node.isText ? node.text : ' ';
        if (isDelete) {
          change.hasDelete = true;
          change.del += text;
        } else {
          change.hasInsert = true;
          change.ins += text;
        }
      });
    }
    return true;
  });
  return Array.from(byId.values())
    .sort((a, b) => a.pos - b.pos)
    .map((change) => {
      const kind = change.hasDelete && change.hasInsert ? 'replace' : change.hasDelete ? 'delete' : 'insert';
      let summary;
      if (kind === 'replace') summary = `Replace "${clip(change.del, 40)}" with "${clip(change.ins, 40)}"`;
      else if (kind === 'delete') summary = `Remove "${clip(change.del)}"`;
      else summary = `Add "${clip(change.ins)}"`;
      return { id: change.id, kind, summary, pos: change.pos };
    });
}

/**
 * Whether a document contains any review markup.
 * @param {import('@tiptap/pm/model').Node} doc - Document.
 * @returns {boolean} True when diff marks or diff block attributes exist.
 */
export function docHasReviewMarkup(doc) {
  let found = false;
  doc.descendants((node) => {
    if (found) return false;
    if (hasDiffBlock(node)) {
      found = true;
      return false;
    }
    if (node.isInline && node.marks.some((m) => m.type.name === 'diffDelete' || m.type.name === 'diffInsert')) {
      found = true;
      return false;
    }
    return true;
  });
  return found;
}

/**
 * Build a transaction that accepts or rejects review changes.
 * Accept: deleted content is removed, inserted content is kept (marks/attrs cleared).
 * Reject: the inverse. Blocks emptied by a deletion are removed so no empty shells remain.
 * @param {import('@tiptap/pm/state').EditorState} state - Current (review) state.
 * @param {Set<string>|null} ids - Change ids to resolve, or null for all.
 * @param {'accept'|'reject'} action - Resolution.
 * @returns {import('@tiptap/pm/state').Transaction} Transaction (not dispatched).
 */
export function buildResolveTransaction(state, ids, action) {
  const { schema } = state;
  const tr = state.tr;
  const matches = (id) => Boolean(id) && (!ids || ids.has(String(id)));
  const removeBlockKind = action === 'accept' ? 'delete' : 'insert';
  const removeMarkType = schema.marks[action === 'accept' ? 'diffDelete' : 'diffInsert'];
  const keepMarkType = schema.marks[action === 'accept' ? 'diffInsert' : 'diffDelete'];

  const blockDeletes = [];
  const inlineDeletes = [];
  const clearAttrs = [];
  const unmark = [];

  state.doc.descendants((node, pos) => {
    if (hasDiffBlock(node) && matches(node.attrs.diffChangeId)) {
      if (node.attrs.diffBlock === removeBlockKind) {
        blockDeletes.push({ from: pos, to: pos + node.nodeSize });
        return false;
      }
      clearAttrs.push(pos);
    }
    if (node.isInline) {
      if (removeMarkType && node.marks.some((m) => m.type === removeMarkType && matches(m.attrs.changeId))) {
        inlineDeletes.push({ from: pos, to: pos + node.nodeSize });
        return false;
      }
      node.marks.forEach((mark) => {
        if (mark.type === keepMarkType && matches(mark.attrs.changeId)) {
          unmark.push({ from: pos, to: pos + node.nodeSize, mark });
        }
      });
    }
    return true;
  });

  // Position-preserving steps first.
  clearAttrs.forEach((pos) => {
    const node = tr.doc.nodeAt(pos);
    if (node) tr.setNodeMarkup(pos, undefined, { ...node.attrs, diffBlock: null, diffChangeId: null }, node.marks);
  });
  unmark.forEach(({ from, to, mark }) => tr.removeMark(from, to, mark));

  // Remember non-empty textblocks touched by inline deletions so emptied shells can be removed.
  const touched = [];
  inlineDeletes.forEach(({ from }) => {
    const $from = state.doc.resolve(from);
    if ($from.parent.isTextblock && $from.depth > 0 && $from.parent.content.size > 0) {
      touched.push($from.before($from.depth));
    }
  });

  const merged = [];
  [...inlineDeletes, ...blockDeletes]
    .sort((a, b) => a.from - b.from)
    .forEach((range) => {
      const last = merged[merged.length - 1];
      if (last && range.from <= last.to) last.to = Math.max(last.to, range.to);
      else merged.push({ ...range });
    });

  for (let i = merged.length - 1; i >= 0; i -= 1) {
    const { from, to } = merged[i];
    const mappedFrom = tr.mapping.map(from, 1);
    const mappedTo = tr.mapping.map(to, -1);
    if (mappedTo <= mappedFrom) continue;
    const coversWholeDoc = mappedFrom <= 0 && mappedTo >= tr.doc.content.size;
    if (coversWholeDoc && schema.nodes.paragraph) {
      tr.replaceWith(0, tr.doc.content.size, schema.nodes.paragraph.create());
    } else {
      tr.deleteRange(mappedFrom, mappedTo);
    }
  }

  // Remove textblocks (and list items) left empty by deletions.
  touched
    .map((pos) => tr.mapping.mapResult(pos, 1))
    .filter((result) => !result.deleted)
    .map((result) => result.pos)
    .sort((a, b) => b - a)
    .forEach((pos) => {
      const node = tr.doc.nodeAt(pos);
      if (!node || !node.isTextblock || node.content.size > 0) return;
      const $pos = tr.doc.resolve(pos);
      let from = pos;
      let to = pos + node.nodeSize;
      for (let depth = $pos.depth; depth > 0; depth -= 1) {
        const parent = $pos.node(depth);
        if (parent.childCount !== 1 || parent.textContent !== '') break;
        if (!/^(listItem|taskItem|bulletList|orderedList|taskList|blockquote)$/.test(parent.type.name)) break;
        from = $pos.before(depth);
        to = $pos.after(depth);
      }
      // Never empty a container that requires content (doc, table cell, list item).
      const container = tr.doc.resolve(from).parent;
      if (container.childCount > 1) tr.delete(from, to);
    });

  tr.setMeta('addToHistory', false);
  tr.setMeta(REVIEW_META, action);
  return tr;
}

/**
 * The review's current baseline: decisions already made are applied, pending inserts are
 * dropped and pending deletes are kept. Used for saving during review and as the starting
 * point for "refine".
 * @param {import('@tiptap/pm/state').EditorState} state - Review state.
 * @returns {import('@tiptap/pm/model').Node} Baseline document.
 */
export function getReviewBaselineDoc(state) {
  return buildResolveTransaction(state, null, 'reject').doc;
}

/**
 * Serialize a document to HTML.
 * @param {import('@tiptap/pm/model').Node} doc - Document.
 * @param {import('@tiptap/pm/model').Schema} schema - Schema.
 * @returns {string} HTML.
 */
export function docToHtml(doc, schema) {
  return getHTMLFromFragment(doc.content, schema);
}

/**
 * Compare two attribute values (attrs may hold arrays/objects, e.g. table column widths).
 * @param {*} a - Value.
 * @param {*} b - Value.
 * @returns {boolean} True when equal.
 */
const attrValueEq = (a, b) => {
  if (a === b) return true;
  if (a == null || b == null || typeof a !== 'object' || typeof b !== 'object') return false;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

/**
 * Whether two documents are identical apart from the `blockId` of their top-level blocks.
 * @param {import('@tiptap/pm/model').Node} a - Document.
 * @param {import('@tiptap/pm/model').Node} b - Document.
 * @returns {boolean} True when only block ids (if anything) differ.
 */
export function sameDocIgnoringBlockIds(a, b) {
  if (!a || !b) return false;
  if (a.eq(b)) return true;
  if (a.childCount !== b.childCount) return false;
  for (let i = 0; i < a.childCount; i += 1) {
    const x = a.child(i);
    const y = b.child(i);
    if (x.type !== y.type || !Mark.sameSet(x.marks, y.marks) || !x.content.eq(y.content)) return false;
    const keys = new Set([...Object.keys(x.attrs), ...Object.keys(y.attrs)]);
    for (const key of keys) {
      if (key !== 'blockId' && !attrValueEq(x.attrs[key], y.attrs[key])) return false;
    }
  }
  return true;
}

/**
 * Remove the empty paragraph TipTap's TrailingNode appends while a review document ends
 * with a non-paragraph block (e.g. an inserted list or heading), when the reference
 * (pre-review) document did not end with one. Otherwise rejecting such a change would leave
 * a stray empty paragraph behind.
 * @param {import('@tiptap/pm/model').Node} doc - Resolved or baseline document.
 * @param {import('@tiptap/pm/model').Node} referenceDoc - Pre-review document.
 * @returns {import('@tiptap/pm/model').Node} Document without the artifact.
 */
export function dropTrailingReviewArtifact(doc, referenceDoc) {
  if (!doc || !referenceDoc || doc.childCount < 2) return doc;
  const isEmptyParagraph = (node) => Boolean(node) && node.type.name === 'paragraph' && node.content.size === 0;
  if (!isEmptyParagraph(doc.lastChild) || isEmptyParagraph(referenceDoc.lastChild)) return doc;
  if (doc.childCount <= referenceDoc.childCount && doc.lastChild.attrs.blockId
    && doc.lastChild.attrs.blockId === referenceDoc.lastChild?.attrs?.blockId) return doc;
  return doc.copy(doc.content.cut(0, doc.content.size - doc.lastChild.nodeSize));
}

const TEMP_ID_PREFIX = '__qrv';

/**
 * Give a review document parsed from HTML (which carries no block ids) the ids of the
 * document it was diffed against, so resolving the review never re-stamps ids:
 *  - blocks that survive "reject all" (unchanged, inline-diffed and deleted blocks) get the id
 *    of the base block in the same position;
 *  - the first inserted block that directly follows a deleted block of the same change
 *    (a block replacement) gets the deleted block's id, so the block keeps its id whether the
 *    change is accepted or rejected (the two share it while the review is open).
 * Other inserted blocks keep no id and are stamped when the review finishes. When the review
 * does not line up with `baseDoc` (it was diffed against other content), it is returned as is.
 * @param {import('@tiptap/pm/model').Node} reviewDoc - Review document.
 * @param {import('@tiptap/pm/model').Node} baseDoc - Document the review was computed from.
 * @returns {import('@tiptap/pm/model').Node} Review document with adopted ids.
 */
export function adoptBaselineBlockIds(reviewDoc, baseDoc) {
  if (!reviewDoc || !baseDoc) return reviewDoc;
  const hasIdAttr = (node) => Object.prototype.hasOwnProperty.call(node.attrs, 'blockId');
  const tagged = [];
  reviewDoc.forEach((node, _pos, index) => {
    tagged.push(hasIdAttr(node)
      ? node.type.create({ ...node.attrs, blockId: `${TEMP_ID_PREFIX}${index}` }, node.content, node.marks)
      : node);
  });
  let rejected;
  try {
    const taggedDoc = reviewDoc.type.create(reviewDoc.attrs, Fragment.fromArray(tagged), reviewDoc.marks);
    rejected = getReviewBaselineDoc(EditorState.create({ doc: taggedDoc }));
  } catch {
    return reviewDoc;
  }
  if (rejected.childCount !== baseDoc.childCount) return reviewDoc;

  const idByIndex = new Map();
  rejected.forEach((node, _pos, k) => {
    const tmp = node.attrs.blockId;
    if (typeof tmp !== 'string' || !tmp.startsWith(TEMP_ID_PREFIX)) return;
    const base = baseDoc.child(k);
    if (base.type !== node.type || !base.attrs.blockId) return;
    idByIndex.set(Number(tmp.slice(TEMP_ID_PREFIX.length)), base.attrs.blockId);
  });
  if (idByIndex.size === 0) return reviewDoc;

  const out = [];
  let lastDelete = null;
  let changed = false;
  reviewDoc.forEach((node, _pos, index) => {
    if (!hasIdAttr(node)) {
      out.push(node);
      lastDelete = null;
      return;
    }
    const kind = node.attrs.diffBlock;
    let id = idByIndex.has(index) ? idByIndex.get(index) : null;
    if (id && kind === 'delete') {
      lastDelete = { changeId: node.attrs.diffChangeId, id, used: false };
    } else if (!id && kind === 'insert' && lastDelete && !lastDelete.used && lastDelete.changeId === node.attrs.diffChangeId) {
      id = lastDelete.id;
      lastDelete.used = true;
    } else if (kind !== 'insert') {
      lastDelete = null;
    }
    if (id && id !== node.attrs.blockId) {
      changed = true;
      out.push(node.type.create({ ...node.attrs, blockId: id }, node.content, node.marks));
    } else {
      out.push(node);
    }
  });
  return changed ? reviewDoc.type.create(reviewDoc.attrs, Fragment.fromArray(out), reviewDoc.marks) : reviewDoc;
}

/**
 * Restore the pre-review state and build the single history step that applies the resolved
 * document. Only the changed range is replaced, so node views outside it are not recreated.
 * @param {import('@tiptap/pm/view').EditorView} view - Editor view.
 * @param {import('@tiptap/pm/state').EditorState} savedState - Pre-review state.
 * @param {import('@tiptap/pm/model').Node} resolvedDoc - Document to keep.
 * @returns {import('@tiptap/pm/state').Transaction} Transaction to dispatch (may have no steps).
 */
export function buildFinalizeTransaction(view, savedState, resolvedDoc) {
  const restored = savedState.reconfigure({ plugins: view.state.plugins });
  view.updateState(restored);
  const tr = view.state.tr;
  const before = restored.doc;
  // Nothing but block ids differs (e.g. everything rejected): keep the saved document as is,
  // so no update/save fires and remembered block ids keep resolving.
  const trimmed = dropTrailingReviewArtifact(resolvedDoc, before);
  const target = sameDocIgnoringBlockIds(before, trimmed) ? before : trimmed;
  const start = before.content.findDiffStart(target.content);
  if (start != null) {
    let { a: endA, b: endB } = before.content.findDiffEnd(target.content);
    const overlap = start - Math.min(endA, endB);
    if (overlap > 0) {
      endA += overlap;
      endB += overlap;
    }
    tr.replace(start, endA, target.slice(start, endB));
  }
  tr.setMeta(REVIEW_META, 'finalize');
  return tr;
}

/**
 * Tag every block/atom element fully inside a diff span with `data-diff-block` so the
 * parsed review document can delete or keep whole nodes (images and diagrams included).
 * @param {string} mergedHtml - HTML with `span[data-diff-delete|insert]` wrappers.
 * @returns {string} Annotated HTML.
 */
export function annotateDiffBlocks(mergedHtml) {
  if (typeof DOMParser === 'undefined' || !mergedHtml) return mergedHtml || '';
  const doc = new DOMParser().parseFromString(`<body>${mergedHtml}</body>`, 'text/html');
  doc.body.querySelectorAll('span[data-diff-delete], span[data-diff-insert]').forEach((span) => {
    const kind = span.hasAttribute('data-diff-delete') ? 'delete' : 'insert';
    const changeId = span.getAttribute('data-change-id');
    if (!changeId) return;
    span.querySelectorAll(BLOCK_SELECTOR).forEach((el) => {
      if (!el.hasAttribute('data-diff-block')) {
        el.setAttribute('data-diff-block', kind);
        el.setAttribute('data-change-id', changeId);
      }
    });
  });
  return doc.body.innerHTML;
}

const TEXTBLOCK_TAGS = new Set(['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6']);
const LIST_TAGS = new Set(['ul', 'ol']);
const BLOCK_CHILD_RE = /<(p|h[1-6]|ul|ol|table|blockquote|pre|div|section|hr|img)\b/i;

/**
 * Comparison key for a block: its HTML without block ids and with collapsed whitespace.
 * @param {string} html - Block HTML.
 * @returns {string} Key.
 */
const blockKey = (html) => html.replace(/\s+data-block-id="[^"]*"/g, '').replace(/\s+/g, ' ').trim();

/**
 * Attributes of an element as a comparable string (block ids ignored).
 * @param {Element} el - Element.
 * @returns {string} Attribute signature.
 */
const attrSignature = (el) => Array.from(el.attributes)
  .filter((a) => a.name !== 'data-block-id')
  .map((a) => `${a.name}=${a.value}`)
  .sort()
  .join('&');

/**
 * Split a container's children into block elements (stray text becomes a paragraph).
 * @param {Element} container - Container element.
 * @param {Document} doc - Owner document.
 * @returns {Element[]} Block elements.
 */
const childBlocks = (container, doc) => {
  const blocks = [];
  Array.from(container.childNodes).forEach((node) => {
    if (node.nodeType === 1) blocks.push(node);
    else if (node.nodeType === 3 && node.textContent.trim()) {
      const p = doc.createElement('p');
      p.textContent = node.textContent;
      blocks.push(p);
    }
  });
  return blocks;
};

/**
 * Opening tag of an element (attributes included).
 * @param {Element} el - Element.
 * @returns {string} Opening tag HTML.
 */
const openTag = (el) => {
  const html = el.cloneNode(false).outerHTML;
  return html.slice(0, html.length - `</${el.tagName.toLowerCase()}>`.length);
};

/**
 * Block-aware review diff. Top-level blocks are aligned first (so diff spans never cross
 * block boundaries); paired paragraphs/headings get an inline token diff, paired lists are
 * diffed item by item, and everything else is shown as whole-block delete + insert using
 * `data-diff-block`. Original block attributes (including block ids) are kept on unchanged
 * and inline-diffed blocks.
 * @param {string} originalHTML - HTML before the edit.
 * @param {string} editedHTML - HTML after the edit.
 * @returns {{ mergedHTML: string, changeCount: number, hasChanges: boolean, metrics: object }} Diff result.
 */
export function buildMergedReviewHtml(originalHTML, editedHTML) {
  const now = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());
  const orig = typeof originalHTML === 'string' ? originalHTML.trim() : '';
  const edit = typeof editedHTML === 'string' ? editedHTML.trim() : '';
  const started = now();
  const baseMetrics = { originalLength: orig.length, editedLength: edit.length, tokenStrategy: 'block-inline' };
  if (!orig || !edit || typeof DOMParser === 'undefined') {
    return { mergedHTML: orig || edit, changeCount: 0, hasChanges: false, metrics: { ...baseMetrics, diffMs: 0 } };
  }
  const parser = new DOMParser();
  const docA = parser.parseFromString(`<body>${orig}</body>`, 'text/html');
  const docB = parser.parseFromString(`<body>${edit}</body>`, 'text/html');
  let changeId = 0;

  /**
   * Tag a whole element as inserted/deleted.
   * @param {Element} el - Element.
   * @param {'insert'|'delete'} kind - Kind.
   * @param {number} cid - Change id.
   * @returns {string} HTML.
   */
  const tagWhole = (el, kind, cid) => {
    const clone = el.cloneNode(true);
    clone.setAttribute('data-diff-block', kind);
    clone.setAttribute('data-change-id', String(cid));
    return clone.outerHTML;
  };

  /**
   * Inline token diff of two inline HTML strings.
   * @param {string} a - Original inline HTML.
   * @param {string} b - Edited inline HTML.
   * @returns {string} Merged inline HTML with diff spans.
   */
  const inlineDiff = (a, b) => {
    const words = (html) => tokenizeHtmlForDiff(html).flatMap((t) => (t.startsWith('<') ? [t] : t.split(/(\s+)/).filter(Boolean)));
    const parts = Diff.diffArrays(words(a), words(b));
    let merged = '';
    for (let i = 0; i < parts.length; i += 1) {
      const part = parts[i];
      const segment = diffPartValueToString(part);
      if (!part.added && !part.removed) {
        merged += segment;
      } else if (part.removed) {
        changeId += 1;
        merged += `<span data-diff-delete="true" data-change-id="${changeId}">${segment}</span>`;
        if (i + 1 < parts.length && parts[i + 1].added) {
          merged += `<span data-diff-insert="true" data-change-id="${changeId}">${diffPartValueToString(parts[i + 1])}</span>`;
          i += 1;
        }
      } else {
        changeId += 1;
        merged += `<span data-diff-insert="true" data-change-id="${changeId}">${segment}</span>`;
      }
    }
    return merged;
  };

  /**
   * Inner inline HTML of a simple list item (`<li>text</li>` or `<li><p>text</p></li>`), else null.
   * @param {Element} li - List item.
   * @returns {{inner: string, wrapper: Element|null}|null} Inline content.
   */
  const simpleItem = (li) => {
    const kids = Array.from(li.children);
    if (!BLOCK_CHILD_RE.test(li.innerHTML)) return { inner: li.innerHTML, wrapper: null };
    if (kids.length === 1 && kids[0].tagName === 'P' && !BLOCK_CHILD_RE.test(kids[0].innerHTML)
      && li.textContent.trim() === kids[0].textContent.trim()) {
      return { inner: kids[0].innerHTML, wrapper: kids[0] };
    }
    return null;
  };

  let diffBlocks = null;

  /**
   * Merge a pair of blocks that occupy the same slot.
   * @param {Element} a - Original block.
   * @param {Element} b - Edited block.
   * @returns {string} Merged HTML.
   */
  const pairBlocks = (a, b) => {
    const tagA = a.tagName.toLowerCase();
    const tagB = b.tagName.toLowerCase();
    const sameShell = tagA === tagB && attrSignature(a) === attrSignature(b);
    if (sameShell && TEXTBLOCK_TAGS.has(tagA) && !BLOCK_CHILD_RE.test(a.innerHTML) && !BLOCK_CHILD_RE.test(b.innerHTML)) {
      return `${openTag(a)}${inlineDiff(a.innerHTML, b.innerHTML)}</${tagA}>`;
    }
    if (sameShell && LIST_TAGS.has(tagA)) {
      return `${openTag(a)}${diffBlocks(childBlocks(a, docA), childBlocks(b, docB))}</${tagA}>`;
    }
    if (sameShell && tagA === 'li') {
      const sa = simpleItem(a);
      const sb = simpleItem(b);
      if (sa && sb && Boolean(sa.wrapper) === Boolean(sb.wrapper)) {
        const merged = inlineDiff(sa.inner, sb.inner);
        return sa.wrapper ? `${openTag(a)}${openTag(sa.wrapper)}${merged}</p></li>` : `${openTag(a)}${merged}</li>`;
      }
    }
    changeId += 1;
    return tagWhole(a, 'delete', changeId) + tagWhole(b, 'insert', changeId);
  };

  /**
   * Align two block lists and merge them.
   * @param {Element[]} blocksA - Original blocks.
   * @param {Element[]} blocksB - Edited blocks.
   * @returns {string} Merged HTML.
   */
  diffBlocks = (blocksA, blocksB) => {
    const parts = Diff.diffArrays(blocksA.map((el) => blockKey(el.outerHTML)), blocksB.map((el) => blockKey(el.outerHTML)));
    let ia = 0;
    let ib = 0;
    let out = '';
    for (let i = 0; i < parts.length; i += 1) {
      const part = parts[i];
      const count = part.count ?? part.value.length;
      if (!part.added && !part.removed) {
        for (let k = 0; k < count; k += 1) out += blocksA[ia + k].outerHTML;
        ia += count;
        ib += count;
      } else if (part.removed) {
        const removed = blocksA.slice(ia, ia + count);
        ia += count;
        let added = [];
        if (i + 1 < parts.length && parts[i + 1].added) {
          const addCount = parts[i + 1].count ?? parts[i + 1].value.length;
          added = blocksB.slice(ib, ib + addCount);
          ib += addCount;
          i += 1;
        }
        const pairs = Math.min(removed.length, added.length);
        for (let k = 0; k < pairs; k += 1) out += pairBlocks(removed[k], added[k]);
        for (let k = pairs; k < removed.length; k += 1) {
          changeId += 1;
          out += tagWhole(removed[k], 'delete', changeId);
        }
        for (let k = pairs; k < added.length; k += 1) {
          changeId += 1;
          out += tagWhole(added[k], 'insert', changeId);
        }
      } else {
        for (let k = 0; k < count; k += 1) {
          changeId += 1;
          out += tagWhole(blocksB[ib + k], 'insert', changeId);
        }
        ib += count;
      }
    }
    return out;
  };

  const blocksA = childBlocks(docA.body, docA);
  const blocksB = childBlocks(docB.body, docB);
  const mergedHTML = diffBlocks(blocksA, blocksB);
  const metrics = { ...baseMetrics, originalBlocks: blocksA.length, editedBlocks: blocksB.length, diffMs: now() - started };
  return { mergedHTML: changeId ? mergedHTML : orig, changeCount: changeId, hasChanges: changeId > 0, metrics };
}

/**
 * Parse review HTML into a document for the editor schema.
 * @param {string} html - Review HTML (diff spans allowed).
 * @param {import('@tiptap/pm/model').Schema} schema - Schema.
 * @returns {import('@tiptap/pm/model').Node} Document.
 */
export function parseReviewHtml(html, schema) {
  return createDocument(annotateDiffBlocks(html), schema);
}
